import React, { useEffect, useState } from "react";
import "../assets/css/callbackModal.css";
import axios from "axios";

const FALLBACK_COUNTRY = {
    name: "India",
    code: "+91",
    flag: "https://flagcdn.com/w40/in.png"
};

function CallbackModal({ product, isOpen, onClose }) {

    /* -------------------- STATE -------------------- */
    const [countries, setCountries] = useState([FALLBACK_COUNTRY]);
    const [selected, setSelected] = useState(FALLBACK_COUNTRY);

    const [step, setStep] = useState(1);

    const [mobile, setMobile] = useState("");
    const [quantity, setQuantity] = useState("");
    const [unit, setUnit] = useState("kg");
    const [purpose, setPurpose] = useState("");
    const [requirement, setRequirement] = useState("");
    const [loading, setLoading] = useState(false)
    const backendUrl = process.env.REACT_APP_BACKEND_URI

    /* -------------------- BODY SCROLL LOCK -------------------- */
    useEffect(() => {
        document.body.style.overflow = isOpen ? "hidden" : "auto";
        return () => (document.body.style.overflow = "auto");
    }, [isOpen]);

    /* -------------------- CHECK LOCALSTORAGE -------------------- */
    useEffect(() => {
        if (isOpen) {
            const saved = localStorage.getItem("callback_lead");
            if (saved) {
                setStep(4); // already submitted
            } else {
                setStep(1);
            }
        }
    }, [isOpen]);

    /* -------------------- FETCH COUNTRIES -------------------- */
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const res = await fetch(
                    "https://restcountries.com/v3.1/all?fields=name,idd,flags"
                );
                const data = await res.json();

                if (!Array.isArray(data)) return;

                const list = data
                    .filter(c => c.idd?.root)
                    .map(c => ({
                        name: c.name.common,
                        code: c.idd.root + (c.idd.suffixes?.[0] || ""),
                        flag: c.flags?.png || ""
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name));

                setCountries(list);
                setSelected(list.find(c => c.code === "+91") || list[0] || FALLBACK_COUNTRY);
            } catch (err) {
                console.warn("Country list could not be loaded. Using fallback country.", err);
                setCountries([FALLBACK_COUNTRY]);
                setSelected(FALLBACK_COUNTRY);
            }
        };

        fetchCountries();
    }, []);

    if (!isOpen) return null;

    /* -------------------- HANDLERS -------------------- */
    const submitMobile = () => {
        if (!mobile || mobile.length < 6) {
            alert("Please enter valid mobile number");
            return;
        }
        setStep(2);
    };

    const submitAskPrice = () => {
        if (!quantity || !purpose) {
            alert("Please fill required details");
            return;
        }
        setStep(3);
    };

    const submitFinal = async () => {
        setLoading(true);

        const payload = {
            productName: product.title,
            mobile,
            countryCode: selected.code,
            quantity,
            unit,
            purpose,
            requirement
        };

        try {
            const res = await axios.post(
                `${backendUrl}/api/product-request`,
                payload,
                {
                    timeout: 8000, // ⏱️ 8 seconds timeout
                    headers: {
                        "Content-Type": "application/json"
                    }
                }
            );

            if (res.status === 200 || res.status === 201) {
                localStorage.setItem("callback_lead", "true"); // ✅ ADD THIS

                setMobile("");
                setQuantity("");
                setUnit("kg");
                setPurpose("");
                setRequirement("");

                setStep(4);
            }

        } catch (error) {
            if (axios.isCancel(error)) {
                alert("Request timed out. Please try again.");
            } else {
                console.error(error);
                alert(
                    error?.response?.data?.message ||
                    "Failed to submit request"
                );
            }
        } finally {
            setLoading(false);
        }
    };

    /* -------------------- JSX -------------------- */
    return (
        <div className="modal-overlay">
            <div className="modal-container">
                <button className="modal-close" onClick={onClose}>✕</button>

                <div className="modal-content">

                    {/* LEFT */}
                    <div className="modal-left">
                        <img
                            src={product.images?.[0]}
                            alt={product.title}
                            className="product-image"
                        />

                        <div className="product-details">
                            <h4>{product.title}</h4>
                            <p>Sold By - Krishna Chemical & Polymer</p>
                        </div>
                    </div>

                    {/* RIGHT */}
                    <div className="modal-right">

                        {/* STEP 1 – MOBILE */}
                        {step === 1 && (
                            <>
                                <h3>Get <span>CALLBACK</span> quickly</h3>

                                <label className="input-label">Mobile Number</label>
                                <div className="phone-field">
                                    <div className="country-selector">
                                        <img src={selected.flag} className="flag-icon" alt="" />
                                        <span className="country-code">{selected.code}</span>

                                        <select
                                            className="country-select"
                                            value={selected.code}
                                            onChange={(e) =>
                                                setSelected(
                                                    countries.find(c => c.code === e.target.value)
                                                )
                                            }
                                        >
                                            {countries.map((c, i) => (
                                                <option key={i} value={c.code}>
                                                    {c.name} ({c.code})
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <input
                                        type="tel"
                                        placeholder="Enter mobile number"
                                        value={mobile}
                                        onChange={(e) => setMobile(e.target.value)}
                                    />
                                </div>

                                <button className="animated-btn submit-btn" onClick={submitMobile}>
                                    <span className="btn-text-wrap">
                                        <span className="btn-text">Submit Now</span>
                                        <span className="btn-text btn-text-abs">Submit Now</span>
                                    </span>
                                </button>
                            </>
                        )}

                        {/* STEP 2 – ASK PRICE */}
                        {step === 2 && (
                            <>
                                <h3>Ask Price by adding a few details</h3>

                                <label className="input-label">Quantity</label>
                                <div className="quantity-field">
                                    <input
                                        type="number"
                                        value={quantity}
                                        onChange={(e) => setQuantity(e.target.value)}
                                    />
                                    <select value={unit} onChange={(e) => setUnit(e.target.value)}>
                                        <option value="kg">kg</option>
                                        <option value="litre">litre</option>
                                    </select>
                                </div>

                                <label className="input-label" style={{ margin: '10px 0 0' }}>Why do you need this?</label>
                                <div className="radio-group">
                                    <label>
                                        <input
                                            type="radio"
                                            name="purpose"
                                            value="Reselling"
                                            onChange={(e) => setPurpose(e.target.value)}
                                        />
                                        For Reselling
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="purpose"
                                            value="Business"
                                            onChange={(e) => setPurpose(e.target.value)}
                                        />
                                        For Business Use
                                    </label>
                                </div>

                                <button className="animated-btn submit-btn" onClick={submitAskPrice}>
                                    <span className="btn-text-wrap">
                                        <span className="btn-text">Next</span>
                                        <span className="btn-text btn-text-abs">Next</span>
                                    </span>
                                </button>
                            </>
                        )}

                        {/* STEP 3 – REQUIREMENT */}
                        {step === 3 && (
                            <>
                                <h3>Almost done!</h3>

                                <label className="input-label">Requirement Details</label>
                                <textarea
                                    className="requirement-box"
                                    placeholder="Describe your requirement in detail"
                                    value={requirement}
                                    onChange={(e) => setRequirement(e.target.value)}
                                />

                                <button className="animated-btn submit-btn" onClick={submitFinal}>
                                    <span className="btn-text-wrap">
                                        <span className="btn-text">{loading ? 'Submitting...' : 'Submit'}</span>
                                        <span className="btn-text btn-text-abs">{loading ? 'Submitting...' : 'Submit'}</span>
                                    </span>
                                </button>
                            </>
                        )}

                        {/* STEP 4 – SUCCESS */}
                        {step === 4 && (
                            <div className="success-box">
                                <h3>✅ Request Submitted</h3>
                                <p>Our team will contact you shortly.</p>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
}

export default CallbackModal;
